import {
  State,
  appLogout,
  createAsyncThunk,
  createSelector,
  createSlice,
  injectSlice,
} from '@bonanza-social/toolkit/state';
import { groupWinnersByRewardId } from './prizeWallWinners.utils';
import { selectPrizeWallId } from '../prizeWall/prizeWall.slice';
import { selectPrizeWallCampaignId } from '../prizeWallCampaign/prizeWallCampaign.slice';
import { PrizeWallWinnersState } from './prizeWallWinners.types';
import PrizeWallCampaignService from '../../services/PrizeWallCampaignService/PrizeWallCampaignService';

export const fetchPrizeWallWinners = createAsyncThunk(
  'prizeWallWinners/fetch',
  (_, thunkApi) => {
    const wallId = selectPrizeWallId(<State>thunkApi.getState());
    const campaignId = selectPrizeWallCampaignId(<State>thunkApi.getState());

    if (campaignId === undefined) {
      throw new Error('Prize campaign id is missing');
    }

    if (wallId === undefined) {
      throw new Error('Prize wall id is missing');
    }

    return PrizeWallCampaignService.getWallWinners({
      pathParams: {
        wallId,
        campaignId,
      },
    });
  },
);

const initialState: PrizeWallWinnersState = {
  data: undefined,
  dataState: 'idle',
};

const prizeWallWinners = createSlice({
  name: 'prizeWallWinners',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchPrizeWallWinners.pending, (state) => {
      state.dataState = 'pending';
    });

    builder.addCase(fetchPrizeWallWinners.fulfilled, (state, { payload }) => {
      state.data = payload;
      state.dataState = 'fulfilled';
    });

    builder.addCase(fetchPrizeWallWinners.rejected, (state) => {
      state.dataState = 'rejected';
    });

    builder.addCase(appLogout, (state) => {
      Object.assign(state, initialState);
    });
  },
});

export const withPrizeWinners = injectSlice({
  reducerPath: 'prizeWallWinners',
  reducer: prizeWallWinners.reducer,
});

export const selectPrizeWallWinnersDataState = withPrizeWinners.selector(
  (state) => state.prizeWallWinners.dataState,
);

export const selectPrizeWallWinnersNextWallStartDate =
  withPrizeWinners.selector(
    (state) => state.prizeWallWinners.data?.nextWallStartDate,
  );

export const selectPrizeWallWinners = withPrizeWinners.selector(
  (state) => state.prizeWallWinners.data?.winners,
);

export const selectPrizeWallWinnersByRewardId = createSelector(
  [selectPrizeWallWinners],
  groupWinnersByRewardId,
);

declare global {
  interface LazyLoadedSlices {
    prizeWallWinners: ReturnType<typeof prizeWallWinners.reducer>;
  }
}
